import { createReducer } from "typesafe-actions";

import ActivityVisibility from "@mapmycustomers/shared/enum/activity/ActivityVisibility";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import Activity from "@mapmycustomers/shared/types/entity/Activity";
import { RawFile } from "@mapmycustomers/shared/types/File";
import Team from "@mapmycustomers/shared/types/Team";
import { UserRef } from "@mapmycustomers/shared/types/User";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";

import localSettings from "@app/config/LocalSettings";
import { Actions as AppActions, initializeApp, reinitializeApp } from "@app/store/app/actions";
import FileListItem from "@app/types/FileListItem";
import activityFieldModel from "@app/util/fieldModel/ActivityFieldModel";
import { preprocessViewStateForActivities } from "@app/util/viewModel/preprocessViewStateForActivities";

import {
  Actions,
  applyListViewSettings,
  bulkDeleteActivities,
  bulkEdit,
  bulkPostpone,
  bulkToggleComplete,
  changeActivityAssignee,
  changeActivityType,
  changeActivityVisibility,
  changeActivityVisibilityTeamsIds,
  deleteActivity,
  fetchFilePreview,
  fetchList,
  openDuplicationModal,
  removeActivityFile,
  subscribeOnViewStateUpdating,
  toggleComplete,
  unsubscribeOnViewStateUpdating,
  updateActivity,
  updateNote,
  uploadActivityFiles,
} from "./actions";

export interface ActivityListViewState extends ViewState {
  activityAssignee?: UserRef;
  activityType?: ActivityType;
  activityVisibility?: ActivityVisibility;
  activityVisibilityTeamIds?: Team["id"][];
  bulkDeleteLoading: boolean;
  bulkEditLoading: boolean;
  bulkPostponeLoading: boolean;
  bulkToggleCompleteLoading: boolean;
  deleteLoading: Activity["id"][];
  error: undefined | unknown;
  filePreview?: Blob;
  filePreviewId?: RawFile["id"];
  filePreviewLoading: boolean;
  filesAdded: FileListItem[];
  filesUploading: boolean;
  loading: boolean;
  onViewStateChanged?: (state: Partial<ViewState>) => void;
  openDuplicationModalLoading: Activity["id"][];
  toggleCompleteLoading: Activity["id"][];
  totalFilteredRecords: number;
  totalRecords: number;
  updateActivityLoading: Activity["id"][];
  updateNoteLoading: Activity["id"][];
}

const initialState: ActivityListViewState = {
  ...activityFieldModel.getDefaultListViewState(),
  activityAssignee: undefined,
  activityType: undefined,
  activityVisibility: undefined,
  bulkDeleteLoading: false,
  bulkEditLoading: false,
  bulkPostponeLoading: false,
  bulkToggleCompleteLoading: false,
  deleteLoading: [],
  error: undefined,
  filePreviewLoading: false,
  filesAdded: [],
  filesUploading: false,
  loading: true,
  openDuplicationModalLoading: [],
  toggleCompleteLoading: [],
  totalFilteredRecords: 0,
  totalRecords: 0,
  updateActivityLoading: [],
  updateNoteLoading: [],
};

const activity = createReducer<ActivityListViewState, Actions | AppActions>(initialState)
  .handleAction([initializeApp.success, reinitializeApp.success], (state) => {
    const viewState = localSettings.getViewSettings("activity/listView", activityFieldModel);
    return {
      ...state,
      ...preprocessViewStateForActivities(viewState),
    };
  })
  .handleAction(applyListViewSettings, (state, { payload }) => ({
    ...state,
    columns: payload.columns ?? state.columns,
    filter: payload.filter ?? state.filter,
    range: payload.range ?? state.range,
    // only update selectedSavedFilterId when it is explicitly present in a payload (even when it is `undefined`)
    selectedSavedFilterId:
      "selectedSavedFilterId" in payload
        ? payload.selectedSavedFilterId
        : state.selectedSavedFilterId,
    sort: payload.sort ?? state.sort,
  }))
  .handleAction(fetchList.request, (state) => ({
    ...state,
    error: undefined,
    loading: true,
  }))
  .handleAction(fetchList.success, (state, { payload }) => ({
    ...state,
    loading: false,
    totalFilteredRecords: payload?.totalFilteredRecords ?? state.totalFilteredRecords,
    totalRecords: payload?.totalRecords ?? state.totalRecords,
  }))
  .handleAction(uploadActivityFiles.request, (state) => ({
    ...state,
    filesUploading: true,
  }))
  .handleAction(uploadActivityFiles.success, (state, action) => ({
    ...state,
    filesAdded: [...state.filesAdded, ...action.payload],
    filesUploading: false,
  }))
  .handleAction(uploadActivityFiles.failure, (state) => ({
    ...state,
    filesUploading: false,
  }))
  .handleAction(removeActivityFile.request, (state) => ({ ...state }))
  .handleAction(removeActivityFile.success, (state, action) => ({
    ...state,
    filesAdded: state.filesAdded.filter((file) => file.uploadedFile?.id !== action.payload.file),
  }))
  .handleAction(fetchList.failure, (state, action) => ({
    ...state,
    error: action.payload,
    loading: false,
  }))
  .handleAction(updateActivity.request, (state, { payload }) => ({
    ...state,
    updateActivityLoading: state.updateActivityLoading.concat(payload.id),
  }))
  .handleAction(updateActivity.success, (state, { payload }) => {
    return {
      ...state,
      updateActivityLoading: state.updateActivityLoading.filter((id) => id !== payload.id),
    };
  })
  .handleAction(updateActivity.failure, (state, action) => ({
    ...state,
    updateActivityLoading: state.updateActivityLoading.filter((id) => id !== action.payload),
  }))
  .handleAction(bulkPostpone.request, (state) => ({
    ...state,
    bulkPostponeLoading: true,
  }))
  .handleAction(bulkPostpone.success, (state) => {
    return { ...state, bulkPostponeLoading: false };
  })
  .handleAction(bulkPostpone.failure, (state) => ({
    ...state,
    bulkPostponeLoading: false,
  }))
  .handleAction(toggleComplete.request, (state, action) => ({
    ...state,
    toggleCompleteLoading: state.toggleCompleteLoading.concat(action.payload.id),
  }))
  .handleAction(toggleComplete.success, (state, { payload }) => ({
    ...state,
    toggleCompleteLoading: state.toggleCompleteLoading.filter((id) => id !== payload.id),
  }))
  .handleAction(toggleComplete.failure, (state, action) => ({
    ...state,
    toggleCompleteLoading: state.toggleCompleteLoading.filter(
      (loadingId) => loadingId !== action.payload
    ),
  }))
  .handleAction(bulkToggleComplete.request, (state) => ({
    ...state,
    bulkToggleCompleteLoading: true,
  }))
  .handleAction(bulkToggleComplete.success, (state) => {
    return { ...state, bulkToggleCompleteLoading: false };
  })
  .handleAction(bulkToggleComplete.failure, (state) => ({
    ...state,
    bulkToggleCompleteLoading: false,
  }))
  .handleAction(openDuplicationModal.request, (state, action) => ({
    ...state,
    openDuplicationModalLoading: state.openDuplicationModalLoading.concat(action.payload),
  }))
  .handleAction(openDuplicationModal.success, (state, action) => ({
    ...state,
    openDuplicationModalLoading: state.openDuplicationModalLoading.filter(
      (loadingId) => loadingId !== action.payload
    ),
  }))
  .handleAction(openDuplicationModal.failure, (state, action) => ({
    ...state,
    openDuplicationModalLoading: state.openDuplicationModalLoading.filter(
      (loadingId) => loadingId !== action.payload
    ),
  }))
  .handleAction(deleteActivity.request, (state, action) => ({
    ...state,
    deleteLoading: state.deleteLoading.concat(action.payload.id),
  }))
  .handleAction(deleteActivity.success, (state, action) => ({
    ...state,
    deleteLoading: state.deleteLoading.filter((loadingId) => loadingId !== action.payload),
  }))
  .handleAction(deleteActivity.failure, (state, action) => ({
    ...state,
    deleteLoading: state.deleteLoading.filter((loadingId) => loadingId !== action.payload),
  }))
  .handleAction(bulkDeleteActivities.request, (state) => ({
    ...state,
    bulkDeleteLoading: true,
  }))
  .handleAction(bulkDeleteActivities.success, (state) => ({
    ...state,
    bulkDeleteLoading: false,
  }))
  .handleAction(bulkDeleteActivities.failure, (state) => ({
    ...state,
    bulkDeleteLoading: false,
  }))
  .handleAction(bulkEdit.request, (state) => ({
    ...state,
    bulkEditLoading: true,
  }))
  .handleAction(bulkEdit.success, (state) => {
    return {
      ...state,
      activityAssignee: undefined,
      activityType: undefined,
      activityVisibility: undefined,
      activityVisibilityTeamIds: undefined,
      bulkEditLoading: false,
    };
  })
  .handleAction(bulkEdit.failure, (state) => ({
    ...state,
    bulkEditLoading: false,
  }))
  .handleAction(updateNote.request, (state, action) => ({
    ...state,
    updateNoteLoading: state.updateNoteLoading.concat(action.payload.activity.id),
  }))
  .handleAction(updateNote.success, (state, action) => ({
    ...state,
    updateNoteLoading: state.updateNoteLoading.filter(
      (loadingId) => loadingId !== action.payload.id
    ),
  }))
  .handleAction(updateNote.failure, (state, action) => ({
    ...state,
    updateNoteLoading: state.updateNoteLoading.filter((loadingId) => loadingId !== action.payload),
  }))
  .handleAction(changeActivityAssignee, (state, action) => ({
    ...state,
    activityAssignee: action.payload,
  }))
  .handleAction(fetchFilePreview.request, (state, action) => ({
    ...state,
    filePreviewId: action.payload.file,
    filePreviewLoading: true,
  }))
  .handleAction(fetchFilePreview.success, (state, action) => ({
    ...state,
    filePreview: action.payload,
    filePreviewLoading: false,
  }))
  .handleAction(fetchFilePreview.failure, (state) => ({
    ...state,
    filePreviewLoading: false,
  }))
  .handleAction(changeActivityVisibilityTeamsIds, (state, action) => ({
    ...state,
    activityVisibilityTeamIds: action.payload,
  }))
  .handleAction(changeActivityType, (state, action) => ({
    ...state,
    activityType: action.payload,
  }))
  .handleAction(changeActivityVisibility, (state, action) => ({
    ...state,
    activityVisibility: action.payload,
  }))
  .handleAction(subscribeOnViewStateUpdating, (state, { payload: { callback } }) => ({
    ...state,
    onViewStateChanged: callback,
  }))
  .handleAction(unsubscribeOnViewStateUpdating, (state) => ({
    ...state,
    onViewStateChanged: undefined,
  }));

export type ActivityActions = Actions;
export default activity;
